import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABEL = { admin: "Gestor Global", staff: "Equipa", client: "Cliente" };

export default function ProfileDialog({ open, onOpenChange }) {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    name: "", email: "", current_password: "", password: "", password_confirm: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && user) {
      setForm({ name: user.name || "", email: user.email || "", current_password: "", password: "", password_confirm: "" });
    }
  }, [open, user]);

  const emailChanged = form.email.trim().toLowerCase() !== (user?.email || "").toLowerCase();
  const wantsNewPassword = !!form.password;
  const requiresCurrent = emailChanged || wantsNewPassword;

  const submit = async (e) => {
    e.preventDefault();
    if (wantsNewPassword && form.password !== form.password_confirm) {
      toast.error("As novas palavras-passe não coincidem");
      return;
    }
    if (wantsNewPassword && form.password.length < 6) {
      toast.error("A nova palavra-passe deve ter pelo menos 6 caracteres");
      return;
    }
    if (requiresCurrent && !form.current_password) {
      toast.error("Confirme a palavra-passe atual");
      return;
    }

    setSaving(true);
    try {
      const payload = { name: form.name };
      if (emailChanged) payload.email = form.email.trim();
      if (wantsNewPassword) payload.password = form.password;
      if (requiresCurrent) payload.current_password = form.current_password;

      await api.put("/auth/me", payload);
      await refresh();
      toast.success("A minha conta atualizada");
      onOpenChange(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#F9F6F0] border border-[#EAE3DB] rounded-2xl max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#1A1412]">A minha conta</DialogTitle>
        </DialogHeader>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B5E3C] mb-2">
          {ROLE_LABEL[user.role] || user.role}
        </div>
        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="profile-form">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Nome</Label>
            <Input
              required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-2 h-11 bg-white border-[#EAE3DB]"
              data-testid="profile-name-input"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Email</Label>
            <Input
              required type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-2 h-11 bg-white border-[#EAE3DB]"
              data-testid="profile-email-input"
            />
          </div>

          <div className="pt-2 border-t border-[#EAE3DB]">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B5E3C] mb-3 mt-3">
              Mudar palavra-passe (opcional)
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Nova palavra-passe</Label>
                <Input
                  type="password" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Deixe vazio para manter"
                  className="mt-2 h-11 bg-white border-[#EAE3DB]"
                  data-testid="profile-new-password-input"
                />
              </div>
              {wantsNewPassword && (
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Confirmar</Label>
                  <Input
                    type="password" value={form.password_confirm}
                    onChange={(e) => setForm({ ...form, password_confirm: e.target.value })}
                    className="mt-2 h-11 bg-white border-[#EAE3DB]"
                    data-testid="profile-confirm-password-input"
                  />
                </div>
              )}
            </div>
          </div>

          {requiresCurrent && (
            <div className="p-3 rounded-xl bg-[#FDF3E1] border border-[#F5E5C8]">
              <Label className="text-[10px] uppercase tracking-[0.3em] text-[#805B10]">
                Palavra-passe atual (confirmação)
              </Label>
              <Input
                required type="password" value={form.current_password}
                onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                className="mt-2 h-11 bg-white border-[#F5E5C8]"
                data-testid="profile-current-password-input"
              />
              <p className="mt-2 text-[10px] text-[#805B10] uppercase tracking-widest">
                Necessária para alterar email ou palavra-passe.
              </p>
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button
              type="button" variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-full border-[#3B2B23] text-[#3B2B23] hover:bg-[#EAE3DB]"
              data-testid="profile-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              type="submit" disabled={saving}
              className="rounded-full bg-[#3B2B23] hover:bg-[#2A1E18] text-[#F9F6F0]"
              data-testid="profile-save-button"
            >
              {saving ? "A guardar…" : "Guardar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
